import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Share2, Sparkles, ScrollText, Swords, Map, Trophy, Star, Compass, Landmark } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";

const CAT_ICON = { skilling: Star, quests: ScrollText, diaries: Map, combat: Swords, raids: Landmark, collection: Trophy, clues: Compass };

export default function TryBoard() {
  const { rsn } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/public/demo/${encodeURIComponent(rsn)}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load that board."); });
    return () => { alive = false; };
  }, [rsn]);

  const share = async () => {
    const url = `https://taskscape.quest/try/${encodeURIComponent(rsn)}`;
    const text = `Check out ${rsn}'s OSRS task board on TaskScape — get your own free one:`;
    try {
      if (navigator.share) await navigator.share({ title: "TaskScape", text, url });
      else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link copied — paste it anywhere!"); }
    } catch {}
  };

  return (
    <div className="stone-bg App flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="flex flex-col items-center mb-6"><Logo variant="hero" /></div>

        {error ? (
          <div className="osrs-panel p-6 text-center" data-testid="tryboard-error">
            <p className="osrs-shadow text-[#FF6666] mb-4">{error}</p>
            <Link to="/login" className="osrs-btn osrs-btn-green px-5 py-2 inline-block">Try your own account →</Link>
          </div>
        ) : !data ? (
          <p className="osrs-shadow text-[#E6A519] text-center py-10">Loading task board...</p>
        ) : (
          <div className="osrs-panel p-6" data-testid="tryboard">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h1 className="osrs-title text-2xl" data-testid="tryboard-rsn">{data.rsn}'s Task Board</h1>
              <button data-testid="tryboard-share" onClick={share} className="osrs-btn px-3 py-1.5 text-xs flex items-center gap-1">
                <Share2 size={14} /> Share
              </button>
            </div>
            <p className="osrs-shadow text-[#b9a06a] text-sm mb-4">Combat {data.combat_level} · Total level {data.total_level}</p>

            <div className="space-y-2 mb-5">
              {data.tasks.map((t, i) => {
                const Icon = CAT_ICON[t.category] || Star;
                return (
                  <div key={i} data-testid="tryboard-task" className="flex items-start gap-3 bg-[#241b10] border-2 border-black p-3">
                    <Icon size={18} className="text-[#7CFC7C] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="osrs-num text-[#FFCF3F] text-sm">
                        {t.title} <span className="text-[#7CFC7C] text-xs">+{t.points}</span>
                        {t.tier && <span className="text-[#b9a06a] text-[10px] ml-1 uppercase">· {t.tier}</span>}
                      </p>
                      <p className="osrs-shadow text-[#b9a06a] text-xs">{t.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Link to="/login" data-testid="tryboard-cta" className="osrs-btn osrs-btn-green w-full py-3 flex items-center justify-center gap-2">
              <Sparkles size={16} /> Get your own free task board →
            </Link>
          </div>
        )}

        <div className="w-full mt-6"><SiteFooter /></div>
      </div>
    </div>
  );
}
